import React from 'react';
import { TrendingDown } from 'lucide-react';

interface FeeBreakdownProps {
  feeStructure: any;
  discountSelection: any;
}

const FeeBreakdownWithDiscounts: React.FC<FeeBreakdownProps> = ({
  feeStructure,
  discountSelection
}) => {
  // Calculate original fees
  const admissionFee = feeStructure.admissionFeeMax || 0;
  const uniformFee = feeStructure.uniformFee || 0;
  const booksAndCopies = feeStructure.booksAndCopies || 0;
  const examFee = feeStructure.examFee || 0;
  const appFee = feeStructure.appFee || 0;
  const monthlyFee = feeStructure.tuitionFee || 0;
  const oneTimeCharges = admissionFee + uniformFee + booksAndCopies + examFee + appFee;

  // Calculate discounts
  let admissionDiscount = 0;
  let monthlyDiscount = 0;
  let booksDiscount = 0;
  let uniformDiscount = 0;

  if (discountSelection && feeStructure.discountPolicy) {
    const policy = feeStructure.discountPolicy;

    // Eligibility-based discounts
    if (discountSelection.orphan) {
      monthlyDiscount += (monthlyFee * policy.orphanMonthlyDiscount) / 100;
      admissionDiscount += (admissionFee * policy.orphanAdmissionDiscount) / 100;
    }

    if (discountSelection.hasSiblings) {
      if (policy.siblingAdmissionDiscount === 100) {
        admissionDiscount = admissionFee; // Waived
      } else {
        admissionDiscount += (admissionFee * policy.siblingAdmissionDiscount) / 100;
      }
      monthlyDiscount += (monthlyFee * policy.siblingMonthlyDiscount) / 100;
    }

    if (discountSelection.staffChild) {
      monthlyDiscount += (monthlyFee * policy.staffChildMonthlyDiscount) / 100;
      admissionDiscount += (admissionFee * policy.staffChildAdmissionDiscount) / 100;
    }

    // Custom discount
    if (discountSelection.customDiscountType === 'admission') {
      admissionDiscount += discountSelection.customDiscountAmount;
    } else if (discountSelection.customDiscountType === 'monthly') {
      monthlyDiscount += (monthlyFee * discountSelection.customDiscountAmount) / 100;
    } else if (discountSelection.customDiscountType === 'books') {
      booksDiscount += discountSelection.customDiscountAmount;
    } else if (discountSelection.customDiscountType === 'uniform') {
      uniformDiscount += discountSelection.customDiscountAmount;
    }
  }

  // Cap discounts to max allowed
  const policy = feeStructure.discountPolicy;
  if (policy) {
    admissionDiscount = Math.min(admissionDiscount, policy.admissionFeeDiscountMax);
    monthlyDiscount = Math.min(monthlyDiscount, (monthlyFee * policy.monthlyFeeDiscountMax) / 100);
    booksDiscount = Math.min(booksDiscount, policy.booksDiscountMax);
    uniformDiscount = Math.min(uniformDiscount, policy.uniformDiscountMax);
  }

  // Calculate final amounts
  const finalAdmissionFee = Math.max(0, admissionFee - admissionDiscount);
  const finalUniformFee = Math.max(0, uniformFee - uniformDiscount);
  const finalBooksAndCopies = Math.max(0, booksAndCopies - booksDiscount);
  const finalOneTimeCharges = finalAdmissionFee + finalUniformFee + finalBooksAndCopies + examFee + appFee;
  const finalMonthlyFee = Math.max(0, monthlyFee - monthlyDiscount);
  const totalDiscount = admissionDiscount + monthlyDiscount + booksDiscount + uniformDiscount;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6 mt-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingDown className="w-5 h-5 text-emerald-600" />
        <h3 className="text-lg font-bold text-slate-800">Fee Breakdown with Discounts</h3>
      </div>

      <div className="space-y-4">
        {/* One-Time Charges */}
        <div className="bg-white rounded-lg p-4">
          <h4 className="font-semibold text-slate-800 mb-3">One-Time Charges</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Admission Fee</span>
              <div className="flex items-center gap-2">
                {admissionDiscount > 0 && (
                  <>
                    <span className="line-through text-slate-400">₹{admissionFee.toLocaleString()}</span>
                    <span className="text-red-600 font-semibold">-₹{admissionDiscount.toLocaleString()}</span>
                  </>
                )}
                <span className="font-bold text-slate-800">₹{finalAdmissionFee.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-600">Uniform (Per Set)</span>
              <div className="flex items-center gap-2">
                {uniformDiscount > 0 && (
                  <>
                    <span className="line-through text-slate-400">₹{uniformFee.toLocaleString()}</span>
                    <span className="text-red-600 font-semibold">-₹{uniformDiscount.toLocaleString()}</span>
                  </>
                )}
                <span className="font-bold text-slate-800">₹{finalUniformFee.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-600">Books/Copies/Diary/ID Card</span>
              <div className="flex items-center gap-2">
                {booksDiscount > 0 && (
                  <>
                    <span className="line-through text-slate-400">₹{booksAndCopies.toLocaleString()}</span>
                    <span className="text-red-600 font-semibold">-₹{booksDiscount.toLocaleString()}</span>
                  </>
                )}
                <span className="font-bold text-slate-800">₹{finalBooksAndCopies.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-600">Exam Fee</span>
              <span className="font-bold text-slate-800">₹{examFee.toLocaleString()}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-600">App Fee (Mandatory)</span>
              <span className="font-bold text-slate-800">₹{appFee.toLocaleString()}</span>
            </div>

            <div className="border-t border-slate-200 pt-2 flex justify-between font-bold">
              <span>Total One-Time Charges</span>
              <span className="text-emerald-600">₹{finalOneTimeCharges.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Monthly Fee */}
        <div className="bg-white rounded-lg p-4">
          <h4 className="font-semibold text-slate-800 mb-3">Monthly Fee</h4>
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Monthly Tuition</span>
            <div className="flex items-center gap-2">
              {monthlyDiscount > 0 && (
                <>
                  <span className="line-through text-slate-400">₹{monthlyFee.toLocaleString()}</span>
                  <span className="text-red-600 font-semibold">-₹{monthlyDiscount.toLocaleString()}</span>
                </>
              )}
              <span className="font-bold text-slate-800">₹{finalMonthlyFee.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Total Discount Summary */}
        {totalDiscount > 0 && (
          <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="font-bold text-emerald-800">Total Discount Applied</span>
              <span className="text-2xl font-bold text-emerald-600">-₹{totalDiscount.toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* First Month Total */}
        <div className="bg-blue-100 border border-blue-300 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <span className="font-bold text-blue-800">First Month Total (One-Time + Monthly)</span>
            <span className="text-2xl font-bold text-blue-600">₹{(finalOneTimeCharges + finalMonthlyFee).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeBreakdownWithDiscounts;
